import type { ModulesResponse, ModuleTarget } from '@/entities/langserver/api/types';

import type { BuildRequest, BuildResponse, ParseRequest, ParseResponse } from '@/features/code-analysis';

export const shouldUseDemoLangServer = (target: ModuleTarget): boolean => {
  return import.meta.env.MODE === 'demo' && target === 'dag';
};

export const shouldUseDemoBuild = (target: ModuleTarget): boolean => {
  return import.meta.env.MODE === 'demo' && (target === 'dag' || target === 'run');
};

export const shouldUseDemoModules = (target: ModuleTarget): boolean => {
  return import.meta.env.MODE === 'demo' && (target === 'dag' || target === 'run');
};

const createDemoDagModulesResponse = (): ModulesResponse => ({
  uploaded: [
    {
      name: 'asset_parser.py',
      module_name: 'asset_parser',
      size: 1814,
    },
    {
      name: 'iceberg_writer.py',
      module_name: 'iceberg_writer',
      size: 3245,
    },
    {
      name: 'parquet_ingestion.py',
      module_name: 'parquet_ingestion',
      size: 1490,
    },
    {
      name: 'table_naming.py',
      module_name: 'table_naming',
      size: 1691,
    },
  ],
  venv_packages: [
    { name: 'apache-airflow', version: '3.2.0', source: 'venv' },
    { name: 'blosc2', version: '4.1.2', source: 'venv' },
    { name: 'boto3', version: '1.42.85', source: 'venv' },
    { name: 'botocore', version: '1.42.85', source: 'venv' },
    { name: 'confluent-kafka', version: '2.14.0', source: 'venv' },
    { name: 'fastavro', version: '1.12.1', source: 'venv' },
    { name: 'kafka-python', version: '2.3.0', source: 'venv' },
    { name: 'onedata', version: '0.7.13', source: 'venv' },
    { name: 'orjson', version: '3.11.8', source: 'venv' },
    { name: 'ormsgpack', version: '1.12.2', source: 'venv' },
    { name: 'pandas', version: '2.3.3', source: 'venv' },
    { name: 'pendulum', version: '3.2.0', source: 'venv' },
    { name: 'psycopg2-binary', version: '2.9.11', source: 'venv' },
    { name: 'pyarrow', version: '18.1.0', source: 'venv' },
    { name: 'pyiceberg', version: '0.10.0', source: 'venv' },
    { name: 'pymongo', version: '4.16.0', source: 'venv' },
    { name: 'python-dateutil', version: '2.9.0.post0', source: 'venv' },
    { name: 'ray', version: '2.51.0', source: 'venv' },
    { name: 'requests', version: '2.33.1', source: 'venv' },
    { name: 'trino', version: '0.330.0', source: 'venv' },
  ],
});

const createDemoRunModulesResponse = (): ModulesResponse => ({
  uploaded: [
    {
      name: 'test_valid_imports.py',
      module_name: 'test_valid_imports',
      size: 3448,
    },
    {
      name: 'write_your_model_experiment.py',
      module_name: 'write_your_model_experiment',
      size: 5404,
    },
  ],
  venv_packages: [
    { name: 'httpx', version: '0.28.1', source: 'venv' },
    { name: 'matplotlib', version: '3.10.8', source: 'venv' },
    { name: 'numpy', version: '2.4.4', source: 'venv' },
    { name: 'pandas', version: '2.3.3', source: 'venv' },
    { name: 'requests', version: '2.33.1', source: 'venv' },
    { name: 'scikit-learn', version: '1.8.0', source: 'venv' },
    { name: 'scipy', version: '1.17.1', source: 'venv' },
    { name: 'torch', version: '2.11.0', source: 'venv' },
  ],
});

export const createDemoModulesResponse = (target: ModuleTarget): ModulesResponse => {
  if (target === 'run') {
    return createDemoRunModulesResponse();
  }

  return createDemoDagModulesResponse();
};

export const createDemoParseResponse = (request: ParseRequest): ParseResponse => ({
  template: [
    {
      type: 'mutable',
      code: request.code,
    },
  ],
  autoImports: [],
  files: request.files ?? {},
});

export const createDemoBuildResponse = (request: BuildRequest): BuildResponse => ({
  code: request.template.map((section) => section.code).join(''),
  files: {},
});
